import { useEffect, useMemo, useState } from 'react';
import { createFollowup, fetchAppointmentAvailability, updateStatus } from '../api';
import { LOST_REASONS } from '../constants/leadReasons';

// Phase B: the one place to record what happened on a call, in 2–3 taps. Saves through the same status route as
// the old form (so Positive still reaches Meta once, Booked still hands over to the centre), closes the lead's old
// "Required before next action" prompt, and can open the next lead in "Waiting for you".

type Reach = 'spoke' | 'no_answer' | 'wrong_number';
type Outcome = 'positive' | 'booked' | 'pending' | 'call_back' | 'not_interested';

const NO_ANSWER = [
  { code: 'did_not_pick', label: 'Did not pick' },
  { code: 'busy_in_another_call', label: 'Busy' },
  { code: 'switch_off', label: 'Switched off' },
  { code: 'call_not_connected', label: 'Not reachable' },
];
const OUTCOMES: Array<{ value: Outcome; label: string; hint: string }> = [
  { value: 'positive', label: 'Positive', hint: 'Likely to come; will decide' },
  { value: 'booked', label: 'Booked', hint: 'Wants a consultation' },
  { value: 'pending', label: 'Undecided', hint: 'Needs information or time' },
  { value: 'call_back', label: 'Call back later', hint: 'Asked us to call at a time' },
  { value: 'not_interested', label: 'Not interested / No issue (Lost)', hint: 'Closes the lead as Lost. Pick the reason, e.g. "No fertility concern / not needed".' },
];
// Most-used Positive/Undecided reasons (codes accepted by the status route), in plain words.
const QUAL_REASONS = [
  { code: 'will_discouse_and_inform_later', label: 'Will discuss at home' },
  { code: 'interested_but_in_some_time', label: 'Interested, later' },
  { code: 'financial_issue', label: 'Cost worry' },
  { code: 'location_issue', label: 'Distance / travel' },
  { code: 'on_behalf_of_family_and_friends', label: 'For family / friend' },
  { code: 'other_reason', label: 'Other' },
];
const CENTRES = ['BBSR', 'BAM', 'Angule'] as const;

export type ResultInput = {
  reach: Reach; noAnswer: string; outcome: Outcome | null; reason: string; note: string;
  date: string; time: string; centre: string; mode: 'Clinic' | 'Phone' | 'Video';
};
// What each choice saves. The status route enforces the rules (reasons, future dates, centre for Booked).
export function resultRequest(r: ResultInput): { status: string; opts: Record<string, unknown>; followup?: { followup_date: string; followup_time: string; notes: string } } {
  if (r.reach === 'wrong_number') return { status: 'invalid_lead', opts: { reason_code: 'incorrect_invalid_number' } };
  if (r.reach === 'no_answer') return { status: 'contacted', opts: { reason_code: r.noAnswer } };
  if (r.outcome === 'not_interested') return { status: 'lost', opts: { reason_code: r.reason, reason_text: r.reason === 'other_reason' ? r.note.trim() : undefined } };
  if (r.outcome === 'call_back') return { status: 'follow_up', opts: { note: 'Patient asked for a call back' },
    followup: { followup_date: r.date, followup_time: r.time, notes: 'Patient asked for a call back' } };
  const qual = r.outcome === 'booked' ? 'booked' : r.outcome === 'pending' ? 'pending' : 'positive';
  return { status: 'qualified', opts: {
    reason_category: 'infertility_issue', reason_code: r.reason, qualification_outcome: qual,
    qualification_outcome_reason: r.note.trim() || undefined,
    ...(qual === 'booked'
      ? { schedule_consultation: true, consultation_date: r.date, consultation_time: r.time, consultation_mode: r.mode, consultation_location: r.centre, center: r.centre || undefined }
      : { next_action_date: r.date, next_action_time: r.time }),
  } };
}

function istDate(offsetDays: number) {
  const d = new Date(Date.now() + 330 * 60_000 + offsetDays * 86_400_000);
  return d.toISOString().slice(0, 10);
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}>
      {children}
    </button>
  );
}

export default function ResultCard({ leadId, lastCallStatus, hint, onSaved }: {
  leadId: string;
  /** call_status of the latest call on this lead, if any ("completed" = patient answered) */
  lastCallStatus: string | null;
  /** WhatsApp assistant's suggested outcome, if any */
  hint: string | null;
  onSaved: (openNext: boolean) => void;
}) {
  const answered = lastCallStatus === 'completed';
  const [reach, setReach] = useState<Reach | null>(lastCallStatus ? (answered ? 'spoke' : 'no_answer') : (hint ? 'spoke' : null));
  const [noAnswer, setNoAnswer] = useState(lastCallStatus && !answered ? 'did_not_pick' : '');
  const [outcome, setOutcome] = useState<Outcome | null>(
    hint === 'likely_positive' ? 'positive' : hint === 'booked' ? 'booked' : hint === 'declined' ? 'not_interested' : null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(istDate(2));
  const [time, setTime] = useState('11:00');
  const [centre, setCentre] = useState<typeof CENTRES[number] | ''>('');
  const [mode, setMode] = useState<'Clinic' | 'Phone' | 'Video'>('Clinic');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [availableSlots, setAvailableSlots] = useState<Array<{ start_time: string; end_time: string | null }>>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [availabilityRefresh, setAvailabilityRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    if (outcome !== 'booked' || mode !== 'Clinic') return () => { active = false; };
    setTime('');
    setAvailableSlots([]);
    setAvailabilityError('');
    if (!centre || !date) return () => { active = false; };
    setAvailabilityLoading(true);
    fetchAppointmentAvailability(centre, date)
      .then((result) => { if (active) setAvailableSlots(result.slots); })
      .catch((err) => { if (active) setAvailabilityError(err instanceof Error ? err.message : 'Could not load available times.'); })
      .finally(() => { if (active) setAvailabilityLoading(false); });
    return () => { active = false; };
  }, [outcome, mode, centre, date, availabilityRefresh]);

  const needsQualReason = outcome === 'positive' || outcome === 'booked' || outcome === 'pending';
  const lostReason = LOST_REASONS.find((r) => r.code === reason);
  const valid = useMemo(() => {
    if (reach === 'wrong_number') return true;
    if (reach === 'no_answer') return Boolean(noAnswer);
    if (reach !== 'spoke' || !outcome) return false;
    if (outcome === 'not_interested') return Boolean(lostReason) && (reason !== 'other_reason' || note.trim().length > 2);
    if (outcome === 'call_back') return Boolean(date && time);
    if (!reason) return false;
    if (outcome === 'pending' && note.trim().length < 3) return false;
    if (outcome === 'booked') return Boolean(centre && date && time && (mode !== 'Clinic' || availableSlots.some((slot) => slot.start_time === time)));
    return Boolean(date && time);
  }, [reach, noAnswer, outcome, reason, note, date, time, centre, mode, availableSlots, lostReason]);

  async function save(openNext: boolean) {
    if (!valid || busy) return;
    setBusy(true); setError('');
    try {
      const req = resultRequest({ reach: reach!, noAnswer, outcome, reason, note, date, time, centre, mode });
      await updateStatus(leadId, req.status as Parameters<typeof updateStatus>[1], req.opts as Parameters<typeof updateStatus>[2]);
      if (req.followup) await createFollowup(leadId, req.followup);
      onSaved(openNext);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
      if (outcome === 'booked' && mode === 'Clinic') setAvailabilityRefresh((value) => value + 1);
    } finally { setBusy(false); }
  }

  return (
    <section aria-label="Record the result" className="rounded-2xl border-2 border-slate-900/10 bg-white p-3 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Record the result</div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Chip active={reach === 'spoke'} onClick={() => setReach('spoke')}>Spoke to patient</Chip>
        <Chip active={reach === 'no_answer'} onClick={() => { setReach('no_answer'); setNoAnswer(noAnswer || 'did_not_pick'); }}>No answer</Chip>
        <Chip active={reach === 'wrong_number'} onClick={() => setReach('wrong_number')}>Wrong number (Reject)</Chip>
      </div>

      {reach === 'wrong_number' && (
        <p className="mt-2 text-xs text-amber-700">Only for a wrong or invalid number. If the person answered and said they have no issue, choose Spoke to patient → Not interested / No issue (Lost).</p>
      )}

      {reach === 'no_answer' && (
        <div className="mt-2 flex flex-wrap gap-2">
          {NO_ANSWER.map((r) => <Chip key={r.code} active={noAnswer === r.code} onClick={() => setNoAnswer(r.code)}>{r.label}</Chip>)}
        </div>
      )}

      {reach === 'spoke' && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            {OUTCOMES.map((o) => (
              <Chip key={o.value} active={outcome === o.value} onClick={() => { setOutcome(o.value); setReason(''); setNote(''); setDate(o.value === 'call_back' ? istDate(1) : istDate(2)); }}>{o.label}</Chip>
            ))}
          </div>
          {outcome && <p className="text-xs text-slate-500">{OUTCOMES.find((o) => o.value === outcome)?.hint}</p>}

          {needsQualReason && (
            <div className="flex flex-wrap gap-2">
              {QUAL_REASONS.map((r) => <Chip key={r.code} active={reason === r.code} onClick={() => setReason(r.code)}>{r.label}</Chip>)}
            </div>
          )}

          {outcome === 'not_interested' && (
            <div className="flex flex-wrap gap-2">
              {LOST_REASONS.map((r) => <Chip key={r.code} active={reason === r.code} onClick={() => setReason(r.code)}>{r.label}</Chip>)}
            </div>
          )}

          {(outcome === 'pending' || (outcome === 'not_interested' && reason === 'other_reason')) && (
            <input id="result-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
              placeholder={outcome === 'pending' ? 'What do they need to decide? (e.g. cost details)' : 'Short reason'}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          )}

          {outcome === 'booked' && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">{CENTRES.map((c) => <Chip key={c} active={centre === c} onClick={() => setCentre(c)}>{c}</Chip>)}</div>
              <div className="flex flex-wrap gap-2">{(['Clinic', 'Phone', 'Video'] as const).map((m) => <Chip key={m} active={mode === m} onClick={() => setMode(m)}>{m}</Chip>)}</div>
            </div>
          )}

          {outcome && outcome !== 'not_interested' && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span>{outcome === 'booked' ? 'Consultation' : outcome === 'call_back' ? 'Call back on' : 'Follow up on'}</span>
              <input id="result-date" type="date" value={date} min={istDate(0)} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5" />
              {outcome === 'booked' && mode === 'Clinic' ? (
                <select id="result-time" value={time} onChange={(e) => setTime(e.target.value)} disabled={availabilityLoading || !!availabilityError || !availableSlots.length} className="rounded-lg border border-slate-200 px-2 py-1.5">
                  <option value="">{availabilityLoading ? 'Checking available times…' : 'Select available time'}</option>
                  {availableSlots.map((slot) => <option key={`${slot.start_time}-${slot.end_time ?? ''}`} value={slot.start_time}>{slot.start_time}{slot.end_time ? `–${slot.end_time}` : ''}</option>)}
                </select>
              ) : (
                <input id="result-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5" />
              )}
            </div>
          )}

          {outcome === 'booked' && mode === 'Clinic' && availabilityError && (
            <div className="flex items-center gap-2 text-xs text-red-700">
              <span>{availabilityError}</span>
              <button type="button" onClick={() => setAvailabilityRefresh((value) => value + 1)} className="font-semibold underline">Retry</button>
            </div>
          )}
          {outcome === 'booked' && mode === 'Clinic' && !availabilityLoading && !availabilityError && centre && date && !availableSlots.length && (
            <p className="text-xs text-slate-600">No appointment slots available for this date.</p>
          )}
        </div>
      )}

      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
      {reach && (
        <div className="mt-3 flex gap-2">
          <button type="button" disabled={!valid || busy} onClick={() => void save(true)}
            className="flex-1 rounded-lg bg-slate-900 py-2 text-sm font-semibold text-white disabled:bg-slate-300">{busy ? 'Saving…' : 'Save & next'}</button>
          <button type="button" disabled={!valid || busy} onClick={() => void save(false)}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40">Save</button>
        </div>
      )}
    </section>
  );
}
